import classNames from "classnames";
import makeStyles from '@mui/styles/makeStyles';
import Backdrop from "@mui/material/Backdrop";
import Grow from "@mui/material/Grow";
import Modal from "@mui/material/Modal";
import {X} from "lucide-react";
import React from "react";
import {color, font, radius, shadow, type} from "common/tokens";

// Shared modal shell: header, scrolling body, optional footer. Used for the
// quick challenge and for the group answers, so both open over the step
// instead of taking the reader somewhere else.
const useStyles = makeStyles({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  paper: {
    transformOrigin: 'center center',
    background: color.SURFACE,
    borderRadius: radius.PANEL,
    boxShadow: shadow.POPOVER,
    width: '100%',
    maxHeight: 'calc(100vh - 96px)',
    display: 'flex',
    flexDirection: 'column',
    outline: 'none',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
    padding: '24px 32px 16px',
    flexShrink: 0,
  },
  heading: {
    flex: 1,
    minWidth: 0,
  },
  // Deliberately quiet: inside a quiz the question is the thing to read, not
  // the name of the dialog it sits in.
  title: {
    ...type.body,
    fontWeight: font.SEMIBOLD,
    color: color.TEXT_MUTED,
    margin: 0,
  },
  titleProminent: {
    ...type.cardHeading,
    color: color.TEXT,
  },
  subtitle: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.TEXT_MUTED,
    margin: '6px 0 0',
  },
  close: {
    appearance: 'none',
    background: 'none',
    border: 'none',
    padding: 4,
    cursor: 'pointer',
    color: color.TEXT_MUTED,
    display: 'flex',
  },
  progressTrack: {
    height: 4,
    margin: '0 32px',
    borderRadius: 4,
    background: color.DIVIDER,
    overflow: 'hidden',
    flexShrink: 0,
  },
  progressFill: {
    display: 'block',
    height: '100%',
    borderRadius: 4,
    background: color.ACCENT,
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: '24px 32px',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '20px 32px 24px',
    borderTop: `1px solid ${color.DIVIDER}`,
    flexShrink: 0,
  },
});

const Dialog = ({
  open = true,
  onClose,
  title,
  subtitle,
  prominentTitle = false,
  progress,
  footer,
  maxWidth = 720,
  dismissable = true,
  children,
}) => {
  const classes = useStyles();

  return (
    <Modal
      open={open}
      onClose={dismissable ? onClose : undefined}
      disableEscapeKeyDown={!dismissable}
      className={classes.modal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{timeout: 220}}>
      <Grow in={open} timeout={{enter: 240, exit: 160}}>
        <div className={classes.paper} style={{maxWidth}}>
          <div className={classes.header}>
            <div className={classes.heading}>
              <h2 className={classNames(classes.title, prominentTitle && classes.titleProminent)}>{title}</h2>
              {Boolean(subtitle) && <p className={classes.subtitle}>{subtitle}</p>}
            </div>
            <button type={'button'} className={classes.close} onClick={onClose} aria-label={'Close'}>
              <X size={20} strokeWidth={2}/>
            </button>
          </div>

          {progress !== undefined && (
            <div className={classes.progressTrack}>
              <span className={classes.progressFill} style={{width: `${progress}%`}}/>
            </div>
          )}

          <div className={classes.body}>{children}</div>

          {Boolean(footer) && <div className={classes.footer}>{footer}</div>}
        </div>
      </Grow>
    </Modal>
  );
};

export default Dialog;
